






































































































import {
  DisplayTotal,
  FormHeader,
  FormPayment,
  FormUnitCode,
} from "@/components/Leasing";
import { useLeasing } from "@/hooks";
import MNotificationVue from "@/mixins/MNotification.vue";
import { Mode } from "@/models/enums/global.enum";
import { LeasingStateEnum } from "@/models/enums/Leasing.enum";
import { LeasingCreateRequestDto } from "@/models/interface/leasing";
import { LeasingEarlySettlementPeriodDto } from "@/models/interface/leasing/LeasingEarlySettlementDto.interface";
import {
  FORM_MODE,
  FormValue,
  RowPeriod,
  State,
} from "@/store/account-payable/leasing/types";
import { FormModel } from "ant-design-vue";
import { Component, Mixins, Prop, Ref } from "vue-property-decorator";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

const NEW_DOCUMENT_FLAG = "-";

@Component({
  data() {
    return {
      FORM_MODE: FORM_MODE,
    };
  },
  components: {
    FormHeader,
    DisplayTotal,
    FormUnitCode,
    FormPayment,
  },
  computed: {
    ...mapState({
      store: (st: any) => st.leasingStore,
    }),
    ...mapGetters({
      isDraft: "leasingStore/isDraft",
    }),
  },
  methods: {
    ...mapMutations({
      setForm: "leasingStore/setForm",
    }),
    ...mapActions({
      getDefaultCurrency: "leasingStore/getDefaultCurrency",
      fetchDetail: "leasingStore/fetchDetail",
    }),
  },
})
export default class FormPage extends Mixins(MNotificationVue) {
  store!: State;
  isDraft!: boolean;
  getDefaultCurrency!: () => void;
  fetchDetail!: (leasingId: string) => Promise<void>;
  setForm!: (value: Partial<FormValue>) => void;

  @Ref("formHeader")
  formHeader!: any;

  @Prop()
  id!: string; // leasing secureId

  assetList: string[] = [];
  mode = "";

  loading = {
    cancel: false,
    submit: false,
    update: false,
    saveDraft: false,
    earlySettlement: false,
  };

  mounted(): void {
    this.getDefaultCurrency();
  }

  get showLeasingButton(): boolean {
    const settledAssets = this.store.form.assetList.filter(
      asset => asset.isSettled
    ).length;
    const totalAssets = this.store.form.assetList.length;

    return (
      settledAssets !== totalAssets &&
      this.store.form.status.toUpperCase() ===
        LeasingStateEnum.SUBMITTED.toUpperCase()
    );
  }

  get isDisabledConfirmSettlement() {
    return this.mode === "" || this.loading.earlySettlement;
  }

  get isDisabledEarlySettlement() {
    return (
      this.mode === FORM_MODE.EARLY_SETTLEMENT || this.loading.earlySettlement
    );
  }

  get isCreate(): boolean {
    const mode = this.$route.meta?.mode || "";
    return mode === Mode.CREATE && !this.id;
  }

  get isEdit(): boolean {
    const mode = this.$route.meta?.mode || "";
    return mode === Mode.EDIT && !!this.id;
  }

  get title(): string {
    const lblEdit = this.$t("lbl_edit_x", {
      x: this.$t("lbl_leasing"),
    }).toString();
    const lblCreate = this.$t("lbl_create_x", {
      x: this.$t("lbl_leasing"),
    }).toString();

    return this.isCreate ? lblCreate : lblEdit;
  }

  handleBack(): void {
    this.$router.push({ name: "account-payables.leasing" });
  }

  validateInstallment(periods: RowPeriod[]): boolean {
    const period = new Set<number>();
    for (const p of periods) {
      period.add(p.paymentCost);
    }

    return period.size > 1;
  }

  handleResetEarlySettlementMode() {
    this.mode = "";
    this.assetList = [];
    const periodList = this.store.form.periodList.filter(
      period => !(period.subInstallment > 0 && !period.id)
    );
    this.setForm({ periodList });
  }

  selectionAssetList(assets: string[]): void {
    this.assetList = assets;
  }

  validatePeriod(): boolean {
    const isValid = this.store.form.periodList.length > 0;
    if (isValid) return true;
    this.showNotifWarning("notif_leasing_period_empty");
    return false;
  }

  validateForm(action: "update" | "submit"): void {
    const formModel: FormModel = this.formHeader.formModel;

    formModel.validate(valid => {
      if (!valid) {
        this.showNotifWarning("notif_validation_error");
        return;
      }

      if (!this.validatePeriod()) {
        return;
      }

      if (this.store.form.assetList.length === 0) {
        this.showNotifWarning("notif_leasing_unit_empty");
        return;
      }

      if (this.validateInstallment(this.store.form.periodList)) {
        this.confirm(() => {
          this.proceed(action);
        });
      } else {
        this.proceed(action);
      }
    });
  }

  proceed(type: "update" | "submit"): void {
    if (type === "update") {
      this.handleUpdate(this.id, this.store.form);
    } else if (type === "submit" && !!this.id) {
      this.handleSubmit(this.id, this.store.form);
    } else if (type === "submit") {
      this.handleSubmit(NEW_DOCUMENT_FLAG, this.store.form);
    }
  }

  handleSubmit(leasingId: string, form: FormValue): void {
    const { submit, mapFormToCreateDto } = useLeasing();
    const req: LeasingCreateRequestDto = mapFormToCreateDto(form);
    this.loading.submit = true;
    submit(leasingId, req)
      .then(({ leasingNumber, id }) => {
        this.showNotifSuccess("notif_submit_success", {
          documentNumber: leasingNumber,
        });
        this.$router.push({
          name: "account-payables.leasing.edit",
          params: { id },
        });
      })
      .finally(() => {
        this.loading.submit = false;
      });
  }

  handleSaveDraft(): void {
    if (!this.validatePeriod()) {
      return;
    }

    const { create, mapFormToCreateDto } = useLeasing();
    const req = mapFormToCreateDto(this.store.form);
    this.loading.saveDraft = true;
    create(req)
      .then(({ id }) => {
        this.showNotifSuccess("notif_create_success");
        this.$router.push({
          name: "account-payables.leasing.edit",
          params: { id },
        });
      })
      .finally(() => {
        this.loading.saveDraft = false;
      });
  }

  handleUpdate(leasingId: string, form: FormValue): void {
    const { update, mapFormToCreateDto } = useLeasing();
    const req: LeasingCreateRequestDto = mapFormToCreateDto(form);
    this.loading.update = true;
    update(leasingId, req)
      .then(() => {
        this.showNotifSuccess("notif_update_success", {
          documentNumber: form.leasingNumber,
        });
        this.fetchDetail(leasingId);
      })
      .finally(() => {
        this.loading.update = false;
      });
  }

  handleCancel(): void {
    const { cancelLeasing } = useLeasing();
    this.loading.cancel = true;
    cancelLeasing(this.id)
      .then(() => {
        this.showNotifSuccess("notif_cancel_success", {
          documentNumber: this.store.form.leasingNumber,
        });
        this.handleBack();
      })
      .finally(() => {
        this.loading.cancel = false;
      });
  }

  confirm(cb: () => void): void {
    this.showConfirmation(this.$t("confirmation.leasing-installment"), cb);
  }

  handleEarlySettlementMode(): void {
    this.mode = FORM_MODE.EARLY_SETTLEMENT;

    window.scrollTo({ top: 1250, left: 0, behavior: "smooth" });
  }

  confirmEarlySettlement(): void {
    this.$confirm({
      title: this.$t("lbl_confirm_settlement"),
      content: this.$t("confirmation.early-settlement"),
      onOk: this.handleEarlySettlement,
    });
  }

  handleEarlySettlement(): void {
    if (this.assetList.length === 0)
      return this.showNotifWarning("validation.leasing-asset-required");
    this.loading.earlySettlement = true;

    const { earlySettlePayment } = useLeasing();

    const periodList: LeasingEarlySettlementPeriodDto[] =
      this.store.form.periodList.reduce((acc, period) => {
        if (!period.cashInOutId) {
          acc.push({
            id: period.id || null,
            parentId: period.id ? null : period.parentId || null,
            principalCost: period.principalCost || 0,
            interestCost: period.interestCost || 0,
            paymentDate: period.paymentDate ? period.paymentDate.format() : null,
            notes: period.notes,
          });
        }
        return acc;
      }, [] as LeasingEarlySettlementPeriodDto[]);

    earlySettlePayment(this.$route.params.id, {
      assetList: this.assetList,
      periodList,
    })
      .then(() => {
        this.showNotifSuccess("notif_early_settlement_success");
        this.handleResetEarlySettlementMode();
        this.fetchDetail(this.$route.params.id);
      })
      .finally(() => {
        this.loading.earlySettlement = false;
      });
  }
}
